/**
 * Minimal Node-style `fs.promises` surface for isomorphic-git, backed by a
 * {@link FileSystemDirectoryHandle} (File System Access API).
 */

import { Buffer as FerossBuffer } from "buffer";

/** Prefer `globalThis.Buffer` (set by `bufferGlobal.ts`) so isomorphic-git’s `Buffer.isBuffer` matches. */
function getBuffer(): typeof FerossBuffer {
  const g = globalThis as typeof globalThis & { Buffer?: typeof FerossBuffer };
  return g.Buffer ?? FerossBuffer;
}

function enoent(path: string): NodeJS.ErrnoException {
  const e = new Error(`ENOENT: ${path}`) as NodeJS.ErrnoException;
  e.code = "ENOENT";
  return e;
}

function enotdir(path: string): NodeJS.ErrnoException {
  const e = new Error(`ENOTDIR: ${path}`) as NodeJS.ErrnoException;
  e.code = "ENOTDIR";
  return e;
}

function eexist(path: string): NodeJS.ErrnoException {
  const e = new Error(`EEXIST: ${path}`) as NodeJS.ErrnoException;
  e.code = "EEXIST";
  return e;
}

type BinaryReadOpts = {
  singleShotMax: number;
  chunkSize: number;
};

const DEFAULT_BIN_READ: BinaryReadOpts = {
  singleShotMax: 24 * 1024 * 1024,
  chunkSize: 8 * 1024 * 1024,
};

/** 2 MiB chunks: fewer async steps than 1 MiB while staying below typical `slice.arrayBuffer()` limits. */
const GIT_PACK_BIN_READ: BinaryReadOpts = {
  singleShotMax: 2 * 1024 * 1024,
  chunkSize: 2 * 1024 * 1024,
};

function isGitPackObjectPath(relKey: string): boolean {
  const k = relKey.replace(/\\/g, "/");
  return /(?:^|\/)objects\/pack\/[^/]+\.pack$/i.test(k) || /^\.git\/objects\/pack\/[^/]+\.pack$/i.test(k);
}

async function readFileAsUint8Array(file: File, opts: BinaryReadOpts = DEFAULT_BIN_READ): Promise<Uint8Array> {
  const { size } = file;
  if (size === 0) return new Uint8Array(0);
  const { singleShotMax, chunkSize } = opts;
  if (size <= singleShotMax) {
    return new Uint8Array(await file.arrayBuffer());
  }
  let out: Uint8Array;
  try {
    out = new Uint8Array(size);
  } catch {
    throw new Error(
      `Cannot allocate ${size} bytes for this file in the browser. The packfile may exceed JS engine limits; try a smaller repo or a shallower clone.`,
    );
  }
  let offset = 0;
  while (offset < size) {
    const end = Math.min(offset + chunkSize, size);
    const slice = file.slice(offset, end);
    const buf = new Uint8Array(await slice.arrayBuffer());
    out.set(buf, offset);
    offset = end;
  }
  return out;
}

/**
 * Binary read into one `Buffer`. Used for `.pack` so isomorphic-git can use it without
 * duplicating a giant `Uint8Array` → `Buffer` copy (see `patches/isomorphic-git+*.patch`).
 */
async function readFileAsBuffer(file: File, opts: BinaryReadOpts = DEFAULT_BIN_READ): Promise<Buffer> {
  const B = getBuffer();
  const { size } = file;
  if (size === 0) return B.alloc(0);
  const { singleShotMax, chunkSize } = opts;
  if (size <= singleShotMax) {
    return B.from(await file.arrayBuffer());
  }
  let out: Buffer;
  try {
    out = B.allocUnsafe(size);
  } catch {
    throw new Error(
      `Cannot allocate ${size} bytes for this file in the browser. The packfile may exceed JS engine limits; try a smaller repo or a shallower clone.`,
    );
  }
  let offset = 0;
  while (offset < size) {
    const end = Math.min(offset + chunkSize, size);
    const slice = file.slice(offset, end);
    const chunk = new Uint8Array(await slice.arrayBuffer());
    out.set(chunk, offset);
    offset = end;
  }
  return out;
}

/** Split a posix-ish path into segments; rejects `..`. Empty → root. */
export function splitPathSegments(rel: string): string[] {
  const s = rel.replace(/\\/g, "/").replace(/^\.\/+/, "").replace(/^\/+/, "");
  if (!s || s === ".") return [];
  const parts = s.split("/").filter((x) => x && x !== ".");
  if (parts.some((x) => x === "..")) {
    const e = new Error("EPERM: ..") as NodeJS.ErrnoException;
    e.code = "EPERM";
    throw e;
  }
  return parts;
}

type StatLike = {
  size: number;
  mode: number;
  dev: number;
  ino: number;
  uid: number;
  gid: number;
  mtimeMs: number;
  mtime: Date;
  ctimeMs: number;
  ctime: Date;
  isFile: () => boolean;
  isDirectory: () => boolean;
  isSymbolicLink: () => boolean;
};

function unixModeForKind(opts: { isFile: boolean; isDirectory: boolean; isSymbolicLink: boolean }): number {
  if (opts.isSymbolicLink) return 0o120000;
  if (opts.isDirectory) return 0o040755;
  return 0o100644;
}

function makeStat(opts: {
  size: number;
  mtimeMs: number;
  isFile: boolean;
  isDirectory: boolean;
  isSymbolicLink: boolean;
}): StatLike {
  const t = opts.mtimeMs;
  const d = new Date(t);
  const mode = unixModeForKind(opts);
  return {
    size: opts.size,
    mode,
    dev: 0,
    ino: 0,
    uid: 0,
    gid: 0,
    mtimeMs: t,
    mtime: d,
    ctimeMs: t,
    ctime: d,
    isFile: () => opts.isFile,
    isDirectory: () => opts.isDirectory,
    isSymbolicLink: () => opts.isSymbolicLink,
  };
}

export function createDirectoryHandleFs(root: FileSystemDirectoryHandle): {
  promises: {
    readFile(path: string, opts?: BufferEncoding | { encoding?: BufferEncoding }): Promise<string | Uint8Array | Buffer>;
    /** Byte range read for large git packfiles (isomorphic-git npp-web patch). */
    readFileRange(path: string, start: number, length: number): Promise<Uint8Array>;
    writeFile(
      path: string,
      data: string | Uint8Array,
      opts?: BufferEncoding | { encoding?: BufferEncoding; mode?: number },
    ): Promise<void>;
    mkdir(path: string, opts?: { recursive?: boolean }): Promise<void>;
    rmdir(path: string, opts?: { recursive?: boolean }): Promise<void>;
    unlink(path: string): Promise<void>;
    readdir(path: string): Promise<string[]>;
    stat(path: string): Promise<StatLike>;
    lstat(path: string): Promise<StatLike>;
    readlink(path: string, opts?: { encoding?: BufferEncoding }): Promise<string>;
    symlink(target: string, path: string): Promise<void>;
    chmod(_path: string, _mode: number): Promise<void>;
    rm(path: string, opts?: { recursive?: boolean; force?: boolean }): Promise<void>;
  };
} {
  const symlinkPaths = new Set<string>();

  const key = (path: string): string => splitPathSegments(path).join("/");

  async function traverse(
    path: string,
    createDirs: number,
  ): Promise<{ dir: FileSystemDirectoryHandle; name: string; isRoot: boolean }> {
    const parts = splitPathSegments(path);
    if (parts.length === 0) {
      return { dir: root, name: "", isRoot: true };
    }
    const fileName = parts[parts.length - 1]!;
    const dirParts = parts.slice(0, -1);
    let d = root;
    for (let i = 0; i < dirParts.length; i++) {
      const seg = dirParts[i]!;
      if (i < createDirs) {
        d = await d.getDirectoryHandle(seg, { create: true });
      } else {
        try {
          d = await d.getDirectoryHandle(seg);
        } catch {
          throw enoent(path);
        }
      }
    }
    return { dir: d, name: fileName, isRoot: false };
  }

  async function writeRaw(path: string, data: string | Uint8Array): Promise<void> {
    const parts = splitPathSegments(path);
    if (parts.length === 0) throw new Error("EISDIR");
    const parentParts = parts.slice(0, -1);
    const leaf = parts[parts.length - 1]!;
    let d = root;
    for (const seg of parentParts) {
      d = await d.getDirectoryHandle(seg, { create: true });
    }
    const fh = await d.getFileHandle(leaf, { create: true });
    const w = await fh.createWritable();
    const u8 = typeof data === "string" ? new TextEncoder().encode(data) : data;
    await w.write(u8);
    await w.close();
  }

  async function statInner(path: string): Promise<StatLike> {
    const parts = splitPathSegments(path);
    if (parts.length === 0) {
      return makeStat({
        size: 0,
        mtimeMs: Date.now(),
        isFile: false,
        isDirectory: true,
        isSymbolicLink: false,
      });
    }
    const k = key(path);
    const { dir, name } = await traverse(path, 0);
    if (symlinkPaths.has(k)) {
      let fh: FileSystemFileHandle;
      try {
        fh = await dir.getFileHandle(name);
      } catch {
        throw enoent(path);
      }
      const file = await fh.getFile();
      const text = await file.text();
      return makeStat({
        size: new TextEncoder().encode(text).byteLength,
        mtimeMs: file.lastModified,
        isFile: false,
        isDirectory: false,
        isSymbolicLink: true,
      });
    }
    try {
      const fh = await dir.getFileHandle(name);
      const file = await fh.getFile();
      return makeStat({
        size: file.size,
        mtimeMs: file.lastModified,
        isFile: true,
        isDirectory: false,
        isSymbolicLink: false,
      });
    } catch {
      /* fall through */
    }
    try {
      await dir.getDirectoryHandle(name);
      return makeStat({
        size: 0,
        mtimeMs: Date.now(),
        isFile: false,
        isDirectory: true,
        isSymbolicLink: false,
      });
    } catch {
      throw enoent(path);
    }
  }

  async function removeRecursive(dh: FileSystemDirectoryHandle): Promise<void> {
    const entries: { name: string; kind: FileSystemHandleKind }[] = [];
    for await (const [name, h] of dh.entries()) {
      entries.push({ name, kind: h.kind });
    }
    for (const { name, kind } of entries) {
      if (kind === "directory") {
        const sub = await dh.getDirectoryHandle(name);
        await removeRecursive(sub);
        await dh.removeEntry(name, { recursive: false });
      } else {
        await dh.removeEntry(name);
      }
    }
  }

  const promises = {
    async readFile(
      path: string,
      opts?: BufferEncoding | { encoding?: BufferEncoding },
    ): Promise<string | Uint8Array | Buffer> {
      const enc = typeof opts === "string" ? opts : opts?.encoding;
      const parts = splitPathSegments(path);
      if (parts.length === 0) throw enoent(path);
      const { dir, name } = await traverse(path, 0);
      let fh: FileSystemFileHandle;
      try {
        fh = await dir.getFileHandle(name);
      } catch {
        throw enoent(path);
      }
      const file = await fh.getFile();
      if (enc === "utf8" || enc === "utf-8") {
        return await file.text();
      }
      const relKey = parts.join("/").replace(/\\/g, "/");
      if (isGitPackObjectPath(relKey)) {
        return readFileAsBuffer(file, GIT_PACK_BIN_READ);
      }
      return readFileAsUint8Array(file, DEFAULT_BIN_READ);
    },

    async readFileRange(path: string, start: number, length: number): Promise<Uint8Array> {
      if (!Number.isFinite(start) || start < 0 || !Number.isFinite(length) || length < 0) {
        throw new Error("readFileRange: invalid start/length");
      }
      const parts = splitPathSegments(path);
      if (parts.length === 0) throw enoent(path);
      const { dir, name } = await traverse(path, 0);
      let fh: FileSystemFileHandle;
      try {
        fh = await dir.getFileHandle(name);
      } catch {
        throw enoent(path);
      }
      const file = await fh.getFile();
      const end = Math.min(start + length, file.size);
      if (end <= start) return new Uint8Array(0);
      const slice = file.slice(start, end);
      return new Uint8Array(await slice.arrayBuffer());
    },

    async writeFile(
      path: string,
      data: string | Uint8Array,
      _opts?: BufferEncoding | { encoding?: BufferEncoding; mode?: number },
    ): Promise<void> {
      symlinkPaths.delete(key(path));
      await writeRaw(path, data);
    },

    async mkdir(path: string, opts?: { recursive?: boolean }): Promise<void> {
      const parts = splitPathSegments(path);
      if (parts.length === 0) return;
      if (opts?.recursive) {
        let d = root;
        for (const seg of parts) {
          d = await d.getDirectoryHandle(seg, { create: true });
        }
        return;
      }
      const parentParts = parts.slice(0, -1);
      const leaf = parts[parts.length - 1]!;
      let d = root;
      for (const seg of parentParts) {
        try {
          d = await d.getDirectoryHandle(seg);
        } catch {
          throw enoent(path);
        }
      }
      try {
        await d.getDirectoryHandle(leaf);
        throw eexist(path);
      } catch (e: unknown) {
        if ((e as NodeJS.ErrnoException).code === "EEXIST") throw e;
        const name = (e as { name?: string }).name;
        if (name === "TypeMismatchError") {
          throw eexist(path);
        }
        if (name === "NotFoundError") {
          await d.getDirectoryHandle(leaf, { create: true });
          return;
        }
        throw e;
      }
    },

    async rmdir(path: string, opts?: { recursive?: boolean }): Promise<void> {
      if (opts?.recursive) {
        await promises.rm(path, { recursive: true, force: true });
        return;
      }
      const parts = splitPathSegments(path);
      if (parts.length === 0) throw new Error("EBUSY: root");
      const parentPath = parts.slice(0, -1).join("/");
      const leaf = parts[parts.length - 1]!;
      const { dir } = parts.length === 1 ? { dir: root } : await traverse(parentPath, 0);
      let dh: FileSystemDirectoryHandle;
      try {
        dh = await dir.getDirectoryHandle(leaf);
      } catch {
        throw enotdir(path);
      }
      for await (const _ of dh.entries()) {
        const e = new Error("ENOTEMPTY") as NodeJS.ErrnoException;
        e.code = "ENOTEMPTY";
        throw e;
      }
      await dir.removeEntry(leaf);
      symlinkPaths.delete(key(path));
    },

    async unlink(path: string): Promise<void> {
      const parts = splitPathSegments(path);
      if (parts.length === 0) throw new Error("EISDIR");
      const { dir, name } = await traverse(path, 0);
      try {
        await dir.removeEntry(name);
      } catch {
        throw enoent(path);
      }
      symlinkPaths.delete(key(path));
    },

    async readdir(path: string): Promise<string[]> {
      const parts = splitPathSegments(path);
      let dh = root;
      for (const seg of parts) {
        try {
          dh = await dh.getDirectoryHandle(seg);
        } catch {
          throw enoent(path);
        }
      }
      const names: string[] = [];
      for await (const [name] of dh.entries()) {
        names.push(name);
      }
      return names.sort();
    },

    stat(path: string): Promise<StatLike> {
      return statInner(path);
    },

    lstat(path: string): Promise<StatLike> {
      return statInner(path);
    },

    async readlink(path: string, opts?: { encoding?: BufferEncoding }): Promise<string> {
      if (opts?.encoding && opts.encoding !== "utf8" && opts.encoding !== "utf-8") {
        throw new Error("unsupported encoding");
      }
      const parts = splitPathSegments(path);
      if (parts.length === 0) throw enoent(path);
      const { dir, name } = await traverse(path, 0);
      try {
        const fh = await dir.getFileHandle(name);
        const file = await fh.getFile();
        return await file.text();
      } catch {
        throw enoent(path);
      }
    },

    async symlink(target: string, path: string): Promise<void> {
      await writeRaw(path, target);
      symlinkPaths.add(key(path));
    },

    chmod(_path: string, _mode: number): Promise<void> {
      return Promise.resolve();
    },

    async rm(path: string, opts?: { recursive?: boolean; force?: boolean }): Promise<void> {
      const parts = splitPathSegments(path);
      if (parts.length === 0) throw new Error("cannot rm root");
      if (opts?.recursive) {
        const { dir, name, isRoot } = await traverse(path, 0);
        if (isRoot) throw new Error("cannot rm root");
        try {
          const sub = await dir.getDirectoryHandle(name);
          await removeRecursive(sub);
          await dir.removeEntry(name, { recursive: false });
        } catch {
          try {
            await dir.removeEntry(name);
          } catch {
            if (!opts.force) throw enoent(path);
          }
        }
        symlinkPaths.delete(key(path));
        return;
      }
      try {
        await promises.unlink(path);
      } catch (e) {
        if (!opts?.force) throw e;
      }
    },
  };

  // Ensure `promises` is enumerable so isomorphic-git’s `FileSystem` binds `fs.promises`
  // (non-enumerable `promises` would skip range reads and force a full `.pack` read).
  const handle = Object.create(null) as { promises: typeof promises };
  Object.defineProperty(handle, "promises", {
    value: promises,
    enumerable: true,
    configurable: true,
    writable: false,
  });
  return handle;
}
